import dialogPolyfill from 'dialog-polyfill';
import { EngineId, BoardUser, GraphMode, NCNode } from '../../../../src/commonTypes';
import { ENGINE_TYPES } from '../../../../src/commonConstants';
import InspireGraph from '../graphs/InspireGraph';
import Graph from '../graphs/Graph';
import { closeAiContainers } from './toolsUI';
import { IdeateGraph } from '../graphs';
import {
	addedWordsFromPanel,
	flowyOnboardingCompleted,
	flowyOnboardingSkipped,
	openedAddToBoardPanel,
	openedProjectVisionPanel,
} from '../tutorials/tutorialEvents';
import { isTutorialActive } from '../tutorials/tutorial';
import { sleep } from '../misc';
import tippy, { Placement, roundArrow } from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/dist/svg-arrow.css';
import {
	enrichOnboardingPhrases,
	getFilesOfBoard,
	getPdfAnalysis,
	PdfAnalysisView,
	synthesiseAi,
	synthesiseProjectFraming,
	updateBoardVisionAndSummary,
} from '../apiRequests';
import { addUserToCollaboratorsList, updateInvitePanelUserCount } from './inviteUser';
import * as tutorialEvents from '../tutorials/tutorialEvents';
import {
	isPdfBoardSeedAlreadyOnBoard,
	selectPdfBoardSeedInsights,
} from '../../../../src/services/pdf/pdfBoardSeedSelection';
import { formatPdfDisplayName } from '../../../../src/pdfFileName';

type OnboardingPhrasePrefetch = Awaited<ReturnType<typeof enrichOnboardingPhrases>>;

let onboardingPhrasePrefetch:
	| {
			key: string;
			promise: Promise<OnboardingPhrasePrefetch>;
	  }
	| undefined;

const normalizeOnboardingSeedKey = ({
	boardId,
	anchorText,
	input,
	vision,
	summary,
}: {
	boardId: string;
	anchorText: string;
	input: string;
	vision: string;
	summary: string;
}): string =>
	[boardId, anchorText, input, vision, summary]
		.map((value) => value.trim().replace(/\s+/g, ' '))
		.join('|');

export const addPdfInsightRingToBoard = (
	inspireGraph: Graph,
	analysis: PdfAnalysisView,
	options: { suppressStatus?: boolean; includeInsights?: boolean } = {}
): void => {
	const displayName = formatPdfDisplayName(analysis.fileName) || 'PDF document';
	const existingOverviewNode = inspireGraph.nodes.find(
		(node) =>
			node.pdfAnalysisId === analysis.analysisId &&
			node.topics?.some((topic) => topic.toLowerCase() === 'pdf-role:overview')
	);
	const pdfOverviewNode =
		existingOverviewNode ||
		inspireGraph.addNodeNearPoint(
			{ x: -320 + (inspireGraph.nodes.length % 4) * 220, y: 260 },
			analysis.overview || `PDF: ${displayName}\n\nAnalysing document insights…`,
			undefined,
			undefined,
			false,
			false,
			{ pdfAnalysisId: analysis.analysisId, pdfFileName: analysis.fileName }
		);
	// The PDF appears immediately as a source card. Once background extraction
	// finishes, replace its placeholder copy and add the selected insight ring.
	if (!analysis.overview) {
		pdfOverviewNode.topics = ['source:pdf', 'pdf-role:overview', 'pdf-status:analysing'];
		pdfOverviewNode.pdfFileName = analysis.fileName || pdfOverviewNode.pdfFileName;
		globalThis.neuroCreate.saveNodes?.update([pdfOverviewNode]);
		return;
	}
	if (existingOverviewNode) {
		pdfOverviewNode.label = analysis.overview;
	}
	if (options.includeInsights === false) {
		pdfOverviewNode.topics = ['source:pdf', 'pdf-role:overview'];
		pdfOverviewNode.pdfFileName = analysis.fileName || pdfOverviewNode.pdfFileName;
		globalThis.neuroCreate.saveNodes?.update([pdfOverviewNode]);
		return;
	}
	let addedNodeCount = existingOverviewNode ? 0 : 1;
	pdfOverviewNode.topics = ['source:pdf', 'pdf-role:overview'];
	pdfOverviewNode.pdfFileName = analysis.fileName || pdfOverviewNode.pdfFileName;
	globalThis.neuroCreate.saveNodes?.update([pdfOverviewNode]);
	const existingInsightIds = new Set(
		inspireGraph.nodes
			.map((node) => node.pdfInsightId)
			.filter((insightId): insightId is string => Boolean(insightId))
	);
	for (const insight of selectPdfBoardSeedInsights(analysis.insights || [])) {
		if (existingInsightIds.has(insight.insightId)) continue;
		inspireGraph.addSingleNode(
			pdfOverviewNode,
			insight.label,
			undefined,
			[
				'source:pdf',
				`pdf-kind:${insight.kind}`,
				...(insight.level ? [`pdf-level:${insight.level}`] : []),
			],
			[],
			undefined,
			false,
			undefined,
			{
				pdfInsightId: insight.insightId,
				pdfAnalysisId: analysis.analysisId,
				pdfFileName: analysis.fileName,
				pdfProvenance: insight.provenance,
			}
		);
		existingInsightIds.add(insight.insightId);
		addedNodeCount += 1;
	}
	inspireGraph.zoomToFit();
	if (
		analysis.analysisId &&
		addedNodeCount > 0 &&
		isPdfBoardSeedAlreadyOnBoard({
			nodes: inspireGraph.nodes,
			analysisId: analysis.analysisId,
			insights: analysis.insights || [],
		})
	) {
		tutorialEvents.pdfSummaryInsightsAdded(
			analysis.analysisId,
			formatPdfDisplayName(analysis.fileName),
			options.suppressStatus === true
		);
	}
};

/**
 * Toggles the visibility of a component based on its class
 */
export const componentToggle = (component: Element) => {
	component.classList.toggle('hidden');
};

export const removeChildren = (element: Element) => {
	if (element.isConnected) {
		while (element.firstChild) {
			element.removeChild(element.firstChild);
		}
	}
};

const loadingIndicatorPanel = document.getElementById('loading-indicator');
let isStickyMessage = false;
export const startLoading = async (loadingMessage = 'Loading...', setStickyMessage = false) => {
	if (loadingIndicatorPanel) {
		if (loadingMessage) {
			if (!isStickyMessage || setStickyMessage) {
				loadingIndicatorPanel.querySelector('h3')!.innerText = loadingMessage;
			}
			if (setStickyMessage) {
				isStickyMessage = true;
			}
		}
		loadingIndicatorPanel.classList.remove('hidden');
		await sleep(0); // Give browser a chance to render the panel before moving on
	}
};

export const finishLoading = () => {
	isStickyMessage = false;
	loadingIndicatorPanel?.classList.add('hidden');
};

export const clearNonBlockingMessage = () => {
	const messageContainer = document.querySelector('.message-show');
	if (messageContainer) {
		const messageWindow = messageContainer.querySelector<HTMLDialogElement>('dialog')!;
		messageWindow.open = false;
		messageContainer.remove();
	}
};

export const clearNonBlockingMessageWithMessage = (message: string) => {
	const messageContainer = document.querySelector('.message-show');
	if (messageContainer && messageContainer.querySelector<HTMLParagraphElement>('.message')!.innerHTML.includes(message)) {
		const messageWindow = messageContainer.querySelector<HTMLDialogElement>('dialog')!;
		messageWindow.open = false;
		messageContainer.remove();
	}
};

export type ButtonDefinition = {
	label: string;
	onClick?: () => void | Promise<void>;
	primary?: boolean;
	iconUrl?: string;
	disabled?: boolean;
	minWidth?: string;
};

export const createButtons = (buttons: Array<ButtonDefinition> = [], isAlert = false) => {
	return buttons.map(({ label, onClick, primary, iconUrl, disabled, minWidth }) => {
		const button = document.createElement('button');
		if (iconUrl) {
			const img = document.createElement('img');
			img.src = iconUrl;
			img.height = 24;
			button.appendChild(img);
		}
		if (minWidth) {
			button.style.minWidth = minWidth;
		}
		button.appendChild(document.createTextNode(label));
		if (primary) {
			button.classList.add('primary');
		}
		if (disabled) {
			button.disabled = true;
		}
		button.addEventListener('click', () => {
			if (isAlert) {
				closeAlert();
			}
			if (onClick) {
				onClick();
			}
		});
		return button;
	});
};

function fadeOutEffect(fadeTarget: HTMLElement) {
	const fadeEffect = setInterval(function () {
		if (!fadeTarget.style.opacity) {
			fadeTarget.style.opacity = '1';
		}
		if (parseFloat(fadeTarget.style.opacity) > 0) {
			fadeTarget.style.opacity = `${parseFloat(fadeTarget.style.opacity) - 0.1}`;
		} else {
			clearInterval(fadeEffect);
			clearNonBlockingMessage();
		}
	}, 200);
}

export const showNonBlockingMessage = (
	message: string,
	buttons: Array<ButtonDefinition> = [],
	fading = false
) => {
	clearNonBlockingMessage();

	const messageWindow = document.createElement('dialog');
	dialogPolyfill.registerDialog(messageWindow);
	messageWindow.open = true;
	messageWindow.classList.add('message-window');

	const nonBlockingMessage = document.createElement('p');
	nonBlockingMessage.classList.add('message');
	nonBlockingMessage.innerHTML = message;
	messageWindow.append(nonBlockingMessage);

	if (buttons.length) {
		const alertButtons = document.createElement('div');
		alertButtons.classList.add('alert-buttons');
		alertButtons.append(...createButtons(buttons));
		messageWindow.append(alertButtons);
	}

	const messageContainer = document.createElement('div');
	messageContainer.classList.add('message-show');
	messageContainer.append(messageWindow);
	document.querySelector('main')!.prepend(messageContainer);

	if (fading) {
		setTimeout(() => {
			fadeOutEffect(messageContainer);
		}, 10000);
	}
};

export const closeAlert = () => {
	const alertContainer = document.querySelector('.alert-show');
	if (alertContainer) {
		const alertWindow = alertContainer.querySelector<HTMLDialogElement>('dialog')!;
		alertContainer.classList.remove('alert-show');
		alertContainer.classList.add('alert-hide');
		alertWindow.open = false;
	}
};

const getCloseButton = (onClose?: () => void) => {
	const closeIcon = document.createElement('img');
	closeIcon.src = '/assets/icon/board/close.png';

	const closeButton = document.createElement('button');
	closeButton.classList.add('alert-close');
	closeButton.appendChild(closeIcon);
	closeButton.addEventListener('click', () => {
		closeAlert();
		onClose?.();
	});
	return closeButton;
};

export const isReadyForNonEssentialAlert = (): boolean => {
	const inviteContainer = document.querySelector('#invite-container')!;
	if (!inviteContainer.classList.contains('hidden')) {
		return false;
	}

	const exportContainer = document.querySelector('#export-container')!;
	if (!exportContainer.classList.contains('hidden')) {
		return false;
	}

	const guideContainer = document.querySelector('#guide')!;
	if (!guideContainer.classList.contains('hidden')) {
		return false;
	}

	const alertContainer = document.querySelector('.alert-show');
	const messageContainer = document.querySelector('.message-show');

	const graph = globalThis.neuroCreate.graph;

	return (
		!alertContainer && !messageContainer && (graph ? !isTutorialActive(graph.graphMode) : true)
	);
};

export const showAlert = (message: string, buttons: Array<ButtonDefinition> = []) => {
	const alertMessage = document.createElement('p');
	alertMessage.classList.add('alert-message');
	alertMessage.innerHTML = message;

	createDialog('', [alertMessage], createButtons(buttons, true));
};

export const showProjectVisionPanel = (input: string) => {
	// Show panel with project vision and project summary fields
	// We will fill these in automatically based on the user's prior input
	// Users will have the choice to edit these fields
	// They can also go back to the previous step to edit their input
	// Or they can hit 'Next' to proceed to the next step

	const messageP = document.createElement('p');
	messageP.classList.add('minimal-message');
	messageP.innerHTML =
		'Based on your previous input, we have generated a project vision and project summary for you. Feel free to edit these as you see fit.';

	const projectVisionHeading = document.createElement('h5');
	projectVisionHeading.innerText = 'Project vision';

	const projectVisionField = document.createElement('text-field');
	projectVisionField.rows = 4;

	const projectSummaryHeading = document.createElement('h5');
	projectSummaryHeading.innerText = 'Project summary';

	const projectSummaryField = document.createElement('text-field');

	setTimeout(() => {
		addToolTipWithTitle(projectVisionField._retryButton);
		addToolTipWithTitle(projectSummaryField._retryButton);
	}, 100);

	const buttons = createButtons(
		[
			{
				label: 'Back',
				disabled: true,
				onClick: () => {
					showWordInputPanel(input);
				},
			},
			{
				label: 'Next',
				onClick: async () => {
					showCentralNodeSelectPanel(
						input,
						projectVisionField._textField.value,
						projectSummaryField._textField.value
					);
					await updateBoardVisionAndSummary(
						document.body.dataset.boardId!,
						input,
						projectVisionField._textField.value,
						projectSummaryField._textField.value
					);
				},
				primary: true,
				disabled: true,
			},
		],
		true
	);

	let firstFramingLoad = true;
	const loadProjectFraming = (() => {
		let promise: Promise<{ vision: string; summary: string }> | null = null;
		return () => {
			if (!promise) {
				const startedAt = Date.now();
				const boardId = document.body.dataset.boardId!;
				console.log('[LLM_PERF]', { stage: 'project_framing:start', boardId });
				promise = synthesiseProjectFraming(boardId, input).then((result) => {
					console.log('[LLM_PERF]', {
						stage: 'project_framing:finish',
						boardId,
						elapsedMs: Date.now() - startedAt,
					});
					return result;
				});
			}
			return promise;
		};
	})();

	projectVisionField.load = async () => {
		if (globalThis.neuroCreate.graph?.getTemplateId() === 'branding') {
			await sleep(1000);
			return Promise.resolve(
				'The project vision is to create a captivating and innovative experience that bridges the worlds of whiskey and music, catering to the needs of enthusiasts looking to explore the legendary connection between these two art forms. By intertwining the rich history of whiskey with the creativity of famous musicians, the project aims to inspire and engage audiences in a unique and memorable way.'
			);
		} else if (firstFramingLoad) {
			return loadProjectFraming().then(({ vision }) => vision);
		} else {
			const startedAt = Date.now();
			const boardId = document.body.dataset.boardId!;
			console.log('[LLM_PERF]', { stage: 'project_vision:start', boardId });
			return synthesiseAi(boardId, 'Vision', input).then(({ result }) => {
				console.log('[LLM_PERF]', {
					stage: 'project_vision:finish',
					boardId,
					elapsedMs: Date.now() - startedAt,
				});
				return result;
			});
		}
	};

	projectSummaryField.load = async () => {
		if (globalThis.neuroCreate.graph?.getTemplateId() === 'branding') {
			await sleep(1000);
			return Promise.resolve(
				'Create an innovative experience explore the legendary connection between whiskey and famous musicians.'
			);
		} else if (firstFramingLoad) {
			return loadProjectFraming().then(({ summary }) => summary);
		} else {
			const startedAt = Date.now();
			const boardId = document.body.dataset.boardId!;
			console.log('[LLM_PERF]', { stage: 'project_summary:start', boardId });
			return synthesiseAi(boardId, 'ProjectSummary', input).then(
				({ result }) => {
					console.log('[LLM_PERF]', {
						stage: 'project_summary:finish',
						boardId,
						elapsedMs: Date.now() - startedAt,
					});
					return result;
				}
			);
		}
	};

	setTimeout(() => {
		Promise.all([projectVisionField.reload(), projectSummaryField.reload()]).then(() => {
			firstFramingLoad = false;
			buttons.forEach((button) => {
				button.removeAttribute('disabled');
			});
			const boardId = document.body.dataset.boardId!;
			const seedStartedAt = Date.now();
			console.log('[LLM_PERF]', { stage: 'board_seed_prefetch:start', boardId });
			const prefetchPayload = {
				anchorText: projectSummaryField._textField.value,
				input,
				vision: projectVisionField._textField.value,
				summary: projectSummaryField._textField.value,
			};
			const prefetchKey = normalizeOnboardingSeedKey({
				boardId,
				...prefetchPayload,
			});
			onboardingPhrasePrefetch = {
				key: prefetchKey,
				promise: enrichOnboardingPhrases(boardId, prefetchPayload),
			};
			void onboardingPhrasePrefetch.promise
				.then((result) => {
					console.log('[LLM_PERF]', {
						stage: 'board_seed_prefetch:finish',
						boardId,
						elapsedMs: Date.now() - seedStartedAt,
						phraseCount: result.phrases.length,
					});
				})
				.catch((error: unknown) => {
					console.warn('[LLM_PERF]', {
						stage: 'board_seed_prefetch:error',
						boardId,
						elapsedMs: Date.now() - seedStartedAt,
						error: error instanceof Error ? error.message : String(error),
					});
				});
		});
	}, 100);

	createDialog(
		'Project vision',
		[
			messageP,
			projectVisionHeading,
			projectVisionField,
			projectSummaryHeading,
			projectSummaryField,
			document.createElement('br'),
			document.createElement('br'),
			document.createElement('br'),
		],
		buttons,
		flowyOnboardingSkipped
	);

	openedProjectVisionPanel();
};

export const createDialog = (
	title: string,
	content: Array<HTMLElement>,
	buttons: Array<HTMLButtonElement>,
	onClose?: () => void
) => {
	const dialog = document.createElement('dialog');
	dialogPolyfill.registerDialog(dialog);
	dialog.open = true;
	dialog.classList.add('alert-window');
	dialog.append(getCloseButton(onClose));

	if (title) {
		const heading = document.createElement('h3');
		heading.innerHTML = title;
		dialog.append(heading);
	}

	dialog.append(...content);

	const alertButtons = document.createElement('div');
	alertButtons.classList.add('alert-buttons');
	alertButtons.append(...buttons);
	dialog.append(alertButtons);

	const windowContainer = document.createElement('div');
	windowContainer.classList.add('alert-show');
	windowContainer.classList.remove('alert-hide');
	windowContainer.append(dialog);

	document.querySelector('main')!.prepend(windowContainer);

	dialog.focus();
};

export const showCentralNodeSelectPanel = (input: string, vision: string, summary: string) => {
	const messageP = document.createElement('p');
	messageP.classList.add('minimal-message');
	messageP.innerHTML =
		'Choose the focus for your board. We will add a central focus node plus eight exploration seeds — each one opens a different lens on your project. Each seed shows a small <strong>lens seed</strong> badge.';

	const projectInputRadio = document.createElement('input');
	projectInputRadio.type = 'radio';
	projectInputRadio.name = 'central-node';
	projectInputRadio.value = input;

	const projectInputLabel = document.createElement('label');
	projectInputLabel.append(projectInputRadio);
	projectInputLabel.append('Your initial input');
	projectInputLabel.classList.add('initial-input');

	const projectInputParagraph = document.createElement('p');
	projectInputParagraph.classList.add('text-preview-full-width');
	projectInputParagraph.innerText = input;

	const projectVisionRadio = document.createElement('input');
	projectVisionRadio.type = 'radio';
	projectVisionRadio.name = 'central-node';
	projectVisionRadio.value = vision;

	const projectVisionLabel = document.createElement('label');
	projectVisionLabel.append(projectVisionRadio);
	projectVisionLabel.append('Project vision');

	const projectVisionParagraph = document.createElement('p');
	projectVisionParagraph.classList.add('text-preview-full-width');
	projectVisionParagraph.innerText = vision;

	const projectSummaryRadio = document.createElement('input');
	projectSummaryRadio.type = 'radio';
	projectSummaryRadio.name = 'central-node';
	projectSummaryRadio.checked = true;
	projectSummaryRadio.value = summary;

	const projectSummaryLabel = document.createElement('label');
	projectSummaryLabel.append(projectSummaryRadio);
	projectSummaryLabel.append('Project summary');

	const projectSummaryParagraph = document.createElement('p');
	projectSummaryParagraph.classList.add('text-preview-full-width');
	projectSummaryParagraph.innerText = summary;

	const isPdfBoard = document.body.dataset.engine === ENGINE_TYPES.CREATE.id;
	const pdfOnly = isPdfBoard && !input.trim() && !vision.trim() && !summary.trim();
	let addToBoardButton: HTMLButtonElement | undefined;
	let refreshPdfOnlySubmitState = (): void => undefined;
	const pdfSelections = document.createElement('div');
	pdfSelections.className = 'pdf-seed-selections';
	const pdfLoadingState = document.createElement('div');
	pdfLoadingState.className = 'pdf-seed-loading';
	pdfLoadingState.innerHTML = '<span aria-hidden="true"></span><p>Analysing PDFs&hellip;</p>';
	pdfSelections.append(pdfLoadingState);
	const getPdfAnalysisStatus = (
		status: string | undefined,
		hasOverview: boolean
	): { label: string; state: 'processing' | 'ready' | 'partial' | 'failed'; title?: string } => {
		if (
			hasOverview &&
			['queued', 'extracting_text', 'extracting_graph'].includes(status || 'queued')
		) {
			return { label: 'Updating analysis', state: 'processing' };
		}
		if (hasOverview && status !== 'failed') {
			return status === 'partial'
				? {
						label: 'Usable · limited coverage',
						state: 'partial',
						title: 'This PDF is usable, but some pages or graph insights could not be fully extracted or validated.',
					}
				: { label: 'Ready', state: 'ready' };
		}

		switch (status) {
			case 'extracting_text':
				return { label: 'Building insights', state: 'processing' };
			case 'extracting_graph':
				return { label: 'Building insights', state: 'processing' };
			case 'partial':
				return {
					label: 'Usable · limited coverage',
					state: 'partial',
					title: 'This PDF is usable, but some pages or graph insights could not be fully extracted or validated.',
				};
			case 'failed':
				return { label: 'Analysis failed', state: 'failed' };
			default:
				return { label: 'Building insights', state: 'processing' };
		}
	};
	const updatePdfSeedCard = (
		fileId: string,
		analysis?: { overview?: string; status?: string; ingestionStatus?: string }
	): void => {
		const card = pdfSelections.querySelector<HTMLElement>(
			`.pdf-seed-card[data-file-id="${CSS.escape(fileId)}"]`
		);
		if (!card) return;

		const overview = analysis?.overview?.trim();
		const status = getPdfAnalysisStatus(
			analysis?.ingestionStatus || analysis?.status,
			Boolean(overview)
		);
		const statusElement = card.querySelector<HTMLElement>('.pdf-seed-card__status');
		const summaryElement = card.querySelector<HTMLElement>('.pdf-seed-card__summary');
		if (statusElement) {
			statusElement.textContent = status.label;
			statusElement.dataset.state = status.state;
			statusElement.title = status.title || '';
		}
		if (summaryElement) {
			summaryElement.textContent =
				overview ||
				(status.state === 'failed'
					? 'We could not analyse this document. Deselect it to continue with the other PDFs.'
					: 'The summary preview will appear here as soon as the document analysis is ready.');
			summaryElement.classList.toggle('is-placeholder', !overview);
		}
	};
	const createPdfSeedCard = (source: Awaited<ReturnType<typeof getFilesOfBoard>>['sources'][number]) => {
		const displayName = formatPdfDisplayName(source.file.fileName);
		const card = document.createElement('label');
		card.className = 'pdf-seed-card is-selected';
		card.dataset.fileId = source.file.fileId;

		const checkbox = document.createElement('input');
		checkbox.type = 'checkbox';
		checkbox.checked = true;
		checkbox.value = source.file.fileId;
		checkbox.dataset.pdfSeed = 'true';
		checkbox.setAttribute('aria-label', `Add ${displayName} to the board`);
		checkbox.addEventListener('change', () => {
			card.classList.toggle('is-selected', checkbox.checked);
			refreshPdfOnlySubmitState();
		});

		const documentIcon = document.createElement('span');
		documentIcon.className = 'pdf-seed-card__icon';
		documentIcon.setAttribute('aria-hidden', 'true');

		const content = document.createElement('span');
		content.className = 'pdf-seed-card__content';

		const header = document.createElement('span');
		header.className = 'pdf-seed-card__header';

		const fileName = document.createElement('strong');
		fileName.className = 'pdf-seed-card__name';
		fileName.textContent = displayName;
		fileName.title = displayName;

		const status = document.createElement('span');
		status.className = 'pdf-seed-card__status';

		const summaryPreview = document.createElement('span');
		summaryPreview.className = 'pdf-seed-card__summary';

		header.append(fileName, status);
		content.append(header, summaryPreview);
		card.append(checkbox, documentIcon, content);

		return card;
	};
	const pdfAnalysisByFileId = new Map<string, PdfAnalysisView>();
	const isPdfAnalysisComplete = (analysis: PdfAnalysisView | undefined): boolean => {
		if (!analysis?.overview?.trim()) return false;
		return Boolean(
			analysis.publishedStatus ||
				['ready', 'partial'].includes(analysis.ingestionStatus || analysis.status)
		);
	};
	refreshPdfOnlySubmitState = (): void => {
		if (!isPdfBoard || !addToBoardButton) return;
		const selected = [
			...pdfSelections.querySelectorAll<HTMLInputElement>(
				'input[data-pdf-seed="true"]:checked'
			),
		];
		const waitingForAnalysis = selected.some(
			(checkbox) => !isPdfAnalysisComplete(pdfAnalysisByFileId.get(checkbox.value))
		);
		// A Create/PDF board can only seed a selected document after its upload
		// has produced a persisted analysis record. Full extraction may continue
		// afterwards, but this prevents the old race where Add to board ran before
		// there was any selectable PDF source to materialise.
		const waitingForSelectableSources =
			!selected.length ||
			selected.some((checkbox) => !pdfAnalysisByFileId.has(checkbox.value));
		addToBoardButton.disabled = pdfOnly
			? !selected.length || waitingForAnalysis
			: waitingForSelectableSources;
		addToBoardButton.title = !selected.length
			? 'Waiting for uploaded PDFs to become available for selection.'
			: !pdfOnly && waitingForSelectableSources
				? 'Wait until PDF analysis begins, then select the documents to add.'
			: waitingForAnalysis
				? 'Wait for all selected PDF analyses to finish.'
				: '';
	};
	let pdfSeedTarget:
		| {
				graph: InspireGraph;
				/** Null means the PDF list was still loading, so all documents are selected. */
				selectedIds: Set<string> | null;
		  }
		| undefined;
	const publishPdfAnalysis = (analysis: PdfAnalysisView): void => {
		const previous = pdfAnalysisByFileId.get(analysis.fileId);
		const boardContentChanged =
			previous?.overview !== analysis.overview ||
			(previous?.insights?.length || 0) !== (analysis.insights?.length || 0);
		pdfAnalysisByFileId.set(analysis.fileId, analysis);
		updatePdfSeedCard(analysis.fileId, analysis);
		refreshPdfOnlySubmitState();
		if (
			boardContentChanged &&
			pdfSeedTarget &&
			globalThis.neuroCreate.graph === pdfSeedTarget.graph &&
			(!pdfSeedTarget.selectedIds || pdfSeedTarget.selectedIds.has(analysis.fileId))
		) {
			// Background completion upgrades only the existing PDF source card.
			// Insights remain available from that card rather than arriving as a
			// second, unsolicited wave of board activity.
			addPdfInsightRingToBoard(pdfSeedTarget.graph, analysis, {
				suppressStatus: true,
				includeInsights: false,
			});
		}
	};
	const trackPdfAnalysis = async (
		fileId: string,
		attemptsRemaining = 150
	): Promise<PdfAnalysisView | undefined> => {
		try {
			const analysis = await getPdfAnalysis(document.body.dataset.boardId!, fileId);
			publishPdfAnalysis(analysis);
			if (
				attemptsRemaining > 0 &&
				['queued', 'extracting_text', 'extracting_graph'].includes(
					analysis.ingestionStatus || analysis.status
				)
			) {
				await sleep(2000);
				return trackPdfAnalysis(fileId, attemptsRemaining - 1);
			}
			return analysis;
		} catch {
			if (attemptsRemaining > 0) {
				await sleep(2000);
				return trackPdfAnalysis(fileId, attemptsRemaining - 1);
			}
			return undefined;
		}
	};
	const waitForCurrentBoardPdfSources = async (attemptsRemaining = 150) => {
		const response = await getFilesOfBoard(document.body.dataset.boardId!);
		const localSources = response.sources.filter(({ currentBoard }) => currentBoard);
		if (
			attemptsRemaining > 0 &&
			!localSources.length &&
			response.uploadStatus?.state === 'processing'
		) {
			await sleep(2000);
			return waitForCurrentBoardPdfSources(attemptsRemaining - 1);
		}
		return localSources;
	};
	const pdfAnalysisPromise: Promise<PdfAnalysisView[]> = isPdfBoard
		? waitForCurrentBoardPdfSources()
				.then(async (localSources) => {
					pdfSelections.replaceChildren();
					if (!localSources.length) {
						const emptyState = document.createElement('p');
						emptyState.className = 'pdf-seed-empty';
						emptyState.textContent = 'No PDFs are available to add to this board.';
						pdfSelections.append(emptyState);
						return [];
					}

					const heading = document.createElement('div');
					heading.className = 'pdf-seed-selections__heading';
					const headingTitle = document.createElement('h4');
					headingTitle.textContent = 'Choose PDF summaries';
					const headingDescription = document.createElement('p');
					headingDescription.textContent =
						'Selected documents can be added when their full analysis and summary are ready.';
					heading.append(headingTitle, headingDescription);

					const cardList = document.createElement('div');
					cardList.className = 'pdf-seed-card-list';
					cardList.append(...localSources.map(createPdfSeedCard));
					pdfSelections.append(heading, cardList);
					for (const source of localSources) {
						updatePdfSeedCard(source.file.fileId, source.analysis);
					}
					refreshPdfOnlySubmitState();

					const analyses = await Promise.all(
						localSources.map(({ file }) => trackPdfAnalysis(file.fileId))
					);
					return analyses.filter(
						(analysis): analysis is PdfAnalysisView => Boolean(analysis)
					);
				})
				.catch(() => [])
		: Promise.resolve([]);
	// Tracking deliberately continues after onboarding closes so the summary node can
	// receive its insight ring when the background graph extraction is published.
	void pdfAnalysisPromise;
	const seedSelectedPdfAnalyses = (graph: InspireGraph): void => {
		const checkboxes = [
			...pdfSelections.querySelectorAll<HTMLInputElement>('input[data-pdf-seed="true"]'),
		];
		const selectedIds = checkboxes.length
			? new Set(
					checkboxes.filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.value)
				)
			: null;
		pdfSeedTarget = { graph, selectedIds };
		for (const analysis of pdfAnalysisByFileId.values()) {
			if (!selectedIds || selectedIds.has(analysis.fileId)) {
				addPdfInsightRingToBoard(graph, analysis, { suppressStatus: true });
			}
		}
	};

	const buttons = createButtons(
		[
			{
				label: 'Add to board',
				disabled: pdfOnly,
				onClick: async () => {
					const selectedText =
						document.querySelector<HTMLInputElement>('input[name="central-node"]:checked')
							?.value || '';
					const inspireGraph = globalThis.neuroCreate.graph as InspireGraph;
					const boardId = document.body.dataset.boardId!;
					await startLoading('Seeding your board...');
					try {
						if (pdfOnly) {
							seedSelectedPdfAnalyses(inspireGraph);
							inspireGraph.zoomToFit();
							addedWordsFromPanel(inspireGraph.nodes);
							flowyOnboardingCompleted({
								input: '',
								vision: '',
								summary: '',
								selectedText: '',
								centralTitle: 'PDF context',
								templateId: inspireGraph.templateId,
								seeds: [],
							});
							return;
						}
						const seedStartedAt = Date.now();
						console.log('[LLM_PERF]', { stage: 'board_seed_add:start', boardId });
						const seedPayload = {
							anchorText: selectedText,
							input,
							vision,
							summary,
						};
						const seedKey = normalizeOnboardingSeedKey({
							boardId,
							...seedPayload,
						});
						const seedResult =
							onboardingPhrasePrefetch?.key === seedKey
								? await onboardingPhrasePrefetch.promise
								: await enrichOnboardingPhrases(boardId, seedPayload);
						const { centralTitle, phrases, useCase } = seedResult;
						console.log('[LLM_PERF]', {
							stage: 'board_seed_add:finish',
							boardId,
							elapsedMs: Date.now() - seedStartedAt,
							phraseCount: phrases.length,
							cacheHit: onboardingPhrasePrefetch?.key === seedKey,
						});
						inspireGraph.addPhrasesToBoard(selectedText, phrases);
						seedSelectedPdfAnalyses(inspireGraph);
						const seeds = phrases
							.map((phrase) => {
								const lensTopic = phrase.topics?.find((topic) => topic.startsWith('lens:'));
								if (!lensTopic) return null;
								return { label: phrase.key, lens: lensTopic.slice('lens:'.length) };
							})
							.filter((seed): seed is { label: string; lens: string } => Boolean(seed));
						addedWordsFromPanel(inspireGraph.nodes);
						// Bootstrap first-layer suggestions before Flowy opens. The panel
						// remains closed because this is board seeding, not a user Spark run.
						await inspireGraph.autoSpark({ deferTutorial: true });
						flowyOnboardingCompleted({
							input,
							vision,
							summary,
							selectedText,
							centralTitle,
							useCase,
							templateId: inspireGraph.templateId,
							seeds,
						});
						inspireGraph.zoomToFit();
						setTimeout(() => {
							inspireGraph.zoomToFit();
						}, 500);
					} catch {
						showAlert('We could not seed phrases right now. Please try again.', [
							{
								label: 'OK',
								primary: true,
								onClick: () => {
									showCentralNodeSelectPanel(input, vision, summary);
								},
							},
						]);
						return;
					} finally {
						finishLoading();
					}
				},
				primary: true,
			},
		],
		true
	);
	addToBoardButton = buttons[0];
	refreshPdfOnlySubmitState();

	const content = pdfOnly
		? [pdfSelections]
		: [
				messageP,
				projectSummaryLabel,
				projectSummaryParagraph,
				projectVisionLabel,
				projectVisionParagraph,
				projectInputLabel,
				projectInputParagraph,
			];
	if (isPdfBoard && !pdfOnly) content.push(pdfSelections);

	createDialog(
		'Add to board',
		content,
		buttons,
		flowyOnboardingSkipped
	);

	openedAddToBoardPanel();
};

export const showWordInputPanel = (input?: string) => {
	const messageP = document.createElement('p');
	messageP.classList.add('minimal-message');
	messageP.innerHTML =
		"Write a paragraph of a minimum of 10 words describing your project, intention, strategy, content or initial ideas you'd like to expand on.";

	const summaryDetails = document.createElement('details');
	const summary = document.createElement('summary');
	summary.innerText = 'What should I write?';
	summaryDetails.innerHTML =
		'This can be a "stream-of-consciousness" activity, where you are making sure to put in the key terms and concepts you\'d like to explore.' +
		'<br><br>' +
		'It can also be more tailored where you can think of yourself as an artist or thematic curator where you can think of your inputs as prompts to shape the AI process, for a symbiotic output.';
	summaryDetails.appendChild(summary);

	const wordCountP = document.createElement('p');
	wordCountP.className = 'word-count';
	wordCountP.innerText = `0 / 10 words`;

	const wordsTextArea = document.createElement('textarea');
	wordsTextArea.rows = 3;
	wordsTextArea.autofocus = true;
	if (input) {
		wordsTextArea.value = input;
	}
	wordsTextArea.oninput = () => {
		const wordcount = wordsTextArea.value.split(' ').filter((s) => s).length;
		wordCountP.innerText = `${wordcount} / 10 words`;
	};

	const buttons = createButtons(
		[
			{
				label: 'Skip',
				onClick: () => {
					if (document.body.dataset.engine === ENGINE_TYPES.CREATE.id) {
						showCentralNodeSelectPanel('', '', '');
					} else {
						tutorialEvents.enableChatbot();
					}
				},
			},
			{
				label: 'Next',
				onClick: () => {
					if (
						document.body.dataset.engine === ENGINE_TYPES.CREATE.id &&
						!wordsTextArea.value.trim()
					) {
						showCentralNodeSelectPanel('', '', '');
					} else {
						showProjectVisionPanel(wordsTextArea.value);
					}
				},
				primary: true,
			},
		],
		true
	);

	createDialog(
		isTutorialActive(globalThis.neuroCreate.graph?.graphMode || 'inspire')
			? `Tutorial: let's begin!`
			: `Let's begin`,
		[messageP, summaryDetails, wordsTextArea, wordCountP],
		buttons,
		flowyOnboardingSkipped
	);
};

export const showTooltip = (message: string) => {
	showAlert(message);
};

export const closeAllPanels = (excludeId = '') => {
	document.querySelectorAll('#top-menu-wrapper button').forEach((button) => {
		if (button !== document.getElementById('timerButton')) {
			button.classList.remove('buttonActive');
		}
	});

	document.querySelectorAll('.panel').forEach((panel) => {
		if (!excludeId || panel.id !== excludeId) {
			panel.classList.add('hidden');
		}
	});
};

export const updateUsersOnlineStatus = (boardUsers: Array<BoardUser>) => {
	const boardUserList = document.querySelector<HTMLUListElement>('.board-user-icon-list')!;
	boardUsers.forEach((boardUser) => {
		const userListItem = document.querySelector<HTMLLIElement>(
			`.board-component-user-item[data-uid="${boardUser.id}"]`
		);
		if (userListItem) {
			const link = userListItem.querySelector<HTMLAnchorElement>('a');
			if (link) {
				if (boardUser.isOnline) {
					userListItem.classList.add('online');
					userListItem.classList.remove('offline');
					link.title = `${boardUser.username} (online)`;
				} else {
					userListItem.classList.add('offline');
					userListItem.classList.remove('online');
					link.title = `${boardUser.username} (offline)`;
				}
			}
		} else {
			const newUserDiv = createUserIconElement(boardUser.username, boardUser.id, boardUser.colour);

			const newUserItem = document.createElement('li');
			newUserItem.className = 'board-component-user-item';
			newUserItem.classList.add(boardUser.isOnline ? 'online' : 'offline');
			newUserItem.dataset.uid = boardUser.id;
			newUserItem.dataset.colour = boardUser.colour;
			newUserItem.dataset.username = boardUser.username;
			newUserItem.append(newUserDiv);

			boardUserList.append(newUserItem);

			addUserToCollaboratorsList(
				boardUser.username,
				boardUser.id,
				boardUser.email,
				createUserIconElement(boardUser.username, boardUser.id, boardUser.colour)
			);
		}
	});
	boardUserList.style.width = `${24 + boardUsers.length * 11}px`;

	// Remove any users that were removed from the board
	const userIds = boardUsers.map((u) => u.id);
	document
		.querySelectorAll<HTMLLIElement>(
			`#toolbar-menu-wrapper .board-component-user-item, #user-with-access li.board-component-user-item`
		)
		.forEach((li) => {
			if (li.dataset.uid && !userIds.includes(li.dataset.uid)) {
				li.remove();
			}
		});
	updateInvitePanelUserCount();
};

export const getUserByColour = (colour: NCNode['colour']) => {
	const userItems = document.querySelectorAll<HTMLLIElement>('.board-component-user-item');
	const ownerUserItem = Array.from(userItems).find((item) => item.dataset.colour === colour);
	if (ownerUserItem) {
		return {
			username: ownerUserItem.dataset.username!,
			uid: ownerUserItem.dataset.uid!,
		};
	}
	return undefined;
};

export const updatedBoardEngine = (engineId: EngineId) => {
	closeAiContainers();
	closeAllPanels();
	// Update Engine icon in board menu
	document.body.dataset.engine = engineId;

	const engineButton = document.querySelector<HTMLButtonElement>('#engine-select')!;
	const img = document.querySelector<HTMLImageElement>('#engine-select img')!;

	engineButton.classList.add(`border-${engineId.toLowerCase()}`);
	img.src = `/assets/icon/dashboard/${engineId.toLowerCase()}.png`;
	img.title = `${
		ENGINE_TYPES[engineId.toUpperCase() as keyof typeof ENGINE_TYPES].name
	} Engine. Click to change engine.`;

	const graph = globalThis.neuroCreate.graph;
	if (graph?.graphMode === 'ideate') {
		const ideateGraph = graph as IdeateGraph;
		ideateGraph.refreshCurrentAnalysis();
	}

	addToolTipWithTitle('#engine-select img');
	setDefaultBoardSettings();
};

export const setDefaultBoardSettings = (fileCount = 0) => {
	void fileCount;

	const { engine: engineId } = document.body.dataset as {
		engine: EngineId;
		openAiFileId: string;
	};

	const elements = [
		{
			button: document.querySelector<HTMLButtonElement>('#board-menu-upload')!,
			// PDF selection is shared board context and remains available in Ideate.
			condition: engineId === ENGINE_TYPES.CREATE.id,
		},
	];

	elements.forEach(({ button, condition }) => {
		if (button) {
			if (condition) {
				button.classList.remove('disabled');
				button.disabled = false;
			} else {
				button.classList.add('disabled');
				button.disabled = true;
			}
		}
	});
};

export const updatedBoardTitle = (title: string) => {
	document.querySelector<HTMLInputElement>('#board-title-form input')!.value = title;
};

export const createUserIconElement = (
	username: string,
	userId: string,
	colour: string
): HTMLDivElement => {
	const newUserLink = document.createElement('a');
	newUserLink.classList.add('no-underline');
	newUserLink.title = username;
	newUserLink.innerHTML = username.substring(0, 1);

	const newUserDiv = document.createElement('div');
	newUserDiv.classList.add('header-menu-icon');
	newUserDiv.classList.add(colour);
	newUserDiv.append(newUserLink);

	return newUserDiv;
};

export const getGraphModeFromHash = (): [mode: GraphMode, isCollaborative: boolean] => {
	switch (window.location.hash) {
		case '#ideate':
			return ['ideate', false];
		case '#ideate-team':
			return ['ideate', true];
		case '#synthesise':
			return ['moodboard', false];
		case '#synthesise-team':
			return ['moodboard', true];
		case '#inspire':
		default:
			return ['inspire', true];
	}
};

export const handleHashRoute = () => {
	const [graphMode, isCollaborative] = getGraphModeFromHash();

	if (globalThis.neuroCreate.switchToMode) {
		globalThis.neuroCreate.switchToMode(graphMode, isCollaborative, false);
	}

	if (window.location.hash === '#collaborators') {
		setTimeout(() => {
			document.querySelector<HTMLButtonElement>('#board-menu-invite')!.click();
		}, 500);
	}
};

export const getBoardIdFromUrl = (inputUrl?: string): string => {
	const url = (inputUrl || window.location.toString()).replace(/#.*$/, '');
	if (!url.includes('/board/')) {
		throw new Error('This is not a board page');
	}
	const boardId = url.substring(url.lastIndexOf('/') + 1);
	return boardId;
};

export const getProjectIdFromUrl = (inputUrl?: string): string => {
	const url = (inputUrl || window.location.toString()).replace(/#.*$/, '');
	if (!url.includes('/project/')) {
		throw new Error('This is not a project page');
	}
	const projectId = url.substring(url.lastIndexOf('/') + 1);
	return projectId;
};

export const addToolTipWithTitle = (
	element: string | HTMLButtonElement | HTMLLabelElement,
	placement: Placement = 'bottom'
): void => {
	// Get elements to add tooltips to
	const elements = typeof element === 'string' ? document.querySelectorAll(element) : [element];

	// Destroy existing tooltips on these elements
	elements.forEach((el) => {
		const existingTooltip = (el as HTMLElement & { _tippy?: { destroy: () => void } })._tippy;
		if (existingTooltip) {
			existingTooltip.destroy();
		}
	});

	// Create new tooltips
	tippy(typeof element === 'string' ? element : [element], {
		content: (reference) => {
			const title = reference.getAttribute('title') || '';
			// reference.removeAttribute('title');
			return title;
		},
		delay: [300, 100],
		offset: placement === 'bottom' ? [0, 15] : [0, 5],
		duration: [1000, 200],
		placement,
		arrow: placement === 'bottom' ? roundArrow : false,
		theme: 'neuro',
		touch: ['hold', 200],
	});
};

// Utils for the chatbot

export const isSparkToolAvailable = (): boolean => {
	return (
		globalThis.neuroCreate.graph?.activeNode !== undefined &&
		globalThis.neuroCreate.graph?.graphMode === 'inspire' &&
		globalThis.neuroCreate.graph
			?.getMenuOptions()
			.find((option) => option.label === 'Spark' && option.enabled) !== undefined
	);
};

/**
 * add tooltips to all elements which have a title attribute
 */
addToolTipWithTitle('[title]:not(#board-zoom-controls button)');
addToolTipWithTitle('#board-zoom-controls button', 'top');

setDefaultBoardSettings();
